import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  KeyRound,
  Plus,
  Trash2,
  Shield,
  Clock,
  Settings as SettingsIcon,
  Eye,
  EyeOff,
  AlertTriangle,
  Plug,
  CheckCircle2,
  XCircle,
  Loader2,
  Globe,
  ChevronDown,
  Database,
  FileText,
  Search,
  Users,
  Zap,
  ShieldCheck,
  Mail,
  Bell,
  Paintbrush,
  Server,
  Fingerprint,
} from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme";
import EnvironmentsTab from "@/components/EnvironmentsTab";
import AuditSystem from "@/components/AuditSystem";
import PassKeyManager from "@/components/PassKeyManager";

export default function SystemPage() {
  const { api, user, hasPermission } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [settings, setSettings] = useState({
    scan_interval_minutes: 15,
    auto_scan_enabled: true,
    orphan_cost_threshold: 50,
    orphan_cooloff_days: 5,
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [credForm, setCredForm] = useState({
    name: "",
    auth_method: "service_principal",
    tenant_id: "",
    client_id: "",
    client_secret: "",
    subscription_id: "",
    certificate_data: "",
  });
  const [showSecret, setShowSecret] = useState(false);
  const [testingCred, setTestingCred] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [oauthStatus, setOauthStatus] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [clearingData, setClearingData] = useState(null);

  const fetchData = async () => {
    try {
      const [credsRes, settingsRes, oauthRes] = await Promise.all([
        api().get("/credentials"),
        api().get("/settings"),
        api().get("/azure-oauth/config").catch(() => ({ data: {} })),
      ]);
      setCredentials(credsRes.data);
      setSettings(settingsRes.data);
      setOauthStatus(oauthRes.data);
    } catch (err) {
      console.error("Failed to fetch settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Handle OAuth callback params
    const oauthSuccess = searchParams.get("oauth_success");
    const oauthError = searchParams.get("oauth_error");
    if (oauthSuccess) {
      toast.success(`${oauthSuccess} Azure subscription(s) connected via OAuth!`);
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (oauthError) {
      toast.error(`OAuth connection failed: ${oauthError}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addCredential = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api().post("/credentials", credForm);
      setCredentials((prev) => [...prev, res.data]);
      setCredForm({
        name: "",
        auth_method: "service_principal",
        tenant_id: "",
        client_id: "",
        client_secret: "",
        subscription_id: "",
        certificate_data: "",
      });
      setShowAddForm(false);
      toast.success("Azure credential added successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add credential");
    } finally {
      setSaving(false);
    }
  };

  const deleteCredential = async (id) => {
    try {
      await api().delete(`/credentials/${id}`);
      setCredentials((prev) => prev.filter((c) => c.id !== id));
      setTestResults((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
      toast.success("Credential deleted");
    } catch (err) {
      toast.error("Failed to delete credential");
    }
  };

  const connectAzure = async () => {
    try {
      const res = await api().get("/azure-oauth/connect");
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        toast.info(res.data.message || res.data.detail || "Scanner App not configured. Run 'Create Customer Auth' from Deployments → Modify first.");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to initiate Azure connection";
      if (msg.includes("Scanner App")) {
        toast.info("Scanner App Registration not configured yet. Go to Deployments → click Modify on your deployment → run 'Create Customer Auth' first.");
      } else {
        toast.error(msg);
      }
    }
  };

  const clearSubscriptionData = async (subscriptionId, credName) => {
    setClearingData(subscriptionId);
    try {
      const res = await api().delete(`/subscriptions/${subscriptionId}/data`);
      toast.success(
        `Cleared ${res.data.resources_deleted} resources and ${res.data.cost_entries_removed} cost entries for ${credName}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear data");
    } finally {
      setClearingData(null);
    }
  };

  const clearAllCosts = async () => {
    try {
      await api().delete("/data/costs");
      toast.success("All cost data cleared");
    } catch (err) {
      toast.error("Failed to clear cost data");
    }
  };

  const testCredential = async (id, name) => {
    setTestingCred(id);
    setTestResults((prev) => ({ ...prev, [id]: null }));
    try {
      const res = await api().post(`/credentials/${id}/test`);
      const costRes = await api().post(`/credentials/${id}/test-cost`);
      const combined = {
        ...res.data,
        cost_access: costRes.data,
      };
      if (res.data.status === "success") {
        combined.message = res.data.message;
        if (costRes.data.status === "success") {
          combined.message += ` | ${costRes.data.message}`;
        } else {
          combined.message += ` | Cost access: ${costRes.data.message}`;
        }
      }
      setTestResults((prev) => ({ ...prev, [id]: combined }));
      if (res.data.status === "success") {
        toast.success(`${name}: Connection OK`);
      } else {
        toast.error(`${name}: ${res.data.message}`, { duration: 8000 });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Test failed";
      setTestResults((prev) => ({
        ...prev,
        [id]: { status: "error", message: msg },
      }));
      toast.error(`${name}: ${msg}`, { duration: 8000 });
    } finally {
      setTestingCred(null);
    }
  };

  const updateSettings = async (updates) => {
    try {
      const res = await api().put("/settings", updates);
      setSettings(res.data);
      toast.success("Settings updated");
    } catch (err) {
      toast.error("Failed to update settings");
    }
  };

  const isAdmin = user?.role === "admin";
  const canWriteCredentials = hasPermission("credentials.write");
  const canDeleteCredentials = hasPermission("credentials.delete");
  const canWriteSettings = hasPermission("settings.write");
  const canClearData = hasPermission("data.clear");
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "credentials";

  // Setup progress calculation
  const setupSteps = [
    { label: "Azure credential added", done: credentials.length > 0 },
    { label: "Credential tested", done: Object.values(testResults).some((r) => r?.status === "success") || credentials.some((c) => c.auth_method === "oauth_consent") },
    { label: "Auto-scan configured", done: settings.auto_scan_enabled },
  ];
  const setupComplete = setupSteps.filter((s) => s.done).length;
  const setupTotal = setupSteps.length;

  return (
    <div className="space-y-6" data-testid="system-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Platform Settings</h1>
          <p className="text-[11px] text-[var(--n-text-muted)] mt-0.5">Manage environments, audit trail & platform preferences</p>
        </div>
        {!loading && setupComplete < setupTotal && (
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--hig-accent)]/[0.04] border border-[var(--hig-accent)]/15" data-testid="setup-progress">
            <div className="flex items-center gap-1.5">
              {setupSteps.map((s, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${s.done ? "bg-emerald-400" : "bg-[var(--n-border-3)]"}`} title={s.label} />
              ))}
            </div>
            <span className="text-[10px] text-[var(--hig-accent)]">{setupComplete}/{setupTotal} setup complete</span>
          </div>
        )}
      </div>

      <Tabs defaultValue="environments" className="space-y-6">
        <TabsList className="bg-[var(--n-card)] border border-[var(--n-border-2)]">
          <TabsTrigger
            value="environments"
            data-testid="environments-tab"
            className="data-[state=active]:bg-[var(--hig-accent-subtle)] data-[state=active]:text-[var(--hig-accent)]"
          >
            <Server className="w-3.5 h-3.5 mr-2" /> Environments
          </TabsTrigger>
          <TabsTrigger
            value="security"
            data-testid="security-tab"
            className="data-[state=active]:bg-[var(--hig-accent-subtle)] data-[state=active]:text-[var(--hig-accent)]"
          >
            <Fingerprint className="w-3.5 h-3.5 mr-2" /> Security
          </TabsTrigger>
          <TabsTrigger
            value="audit"
            data-testid="audit-tab"
            className="data-[state=active]:bg-[var(--hig-accent-subtle)] data-[state=active]:text-[var(--hig-accent)]"
          >
            <FileText className="w-3.5 h-3.5 mr-2" /> Audit
          </TabsTrigger>
          <TabsTrigger
            value="appearance"
            data-testid="appearance-tab"
            className="data-[state=active]:bg-[var(--hig-accent-subtle)] data-[state=active]:text-[var(--hig-accent)]"
          >
            <Paintbrush className="w-3.5 h-3.5 mr-2" /> Appearance
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            data-testid="notifications-tab"
            className="data-[state=active]:bg-[var(--hig-accent-subtle)] data-[state=active]:text-[var(--hig-accent)]"
          >
            <Bell className="w-3.5 h-3.5 mr-2" /> Notifications
          </TabsTrigger>
        </TabsList>

        <TabsContent value="environments" className="space-y-4">
          <EnvironmentsTab api={api} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium mb-1">Authentication</h3>
              <p className="text-xs text-muted-foreground">
                Manage your passwordless sign-in methods. PassKeys use biometrics for secure authentication.
              </p>
            </div>
            <PassKeyManager api={api} />
          </div>
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          {isAdmin ? (
            <AuditSystem api={api} />
          ) : (
            <UserActivityTab api={api} />
          )}
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <NotificationsTab api={api} settings={settings} updateSettings={updateSettings} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="appearance" className="space-y-4">
          <AppearanceTab />
        </TabsContent>

      </Tabs>

      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="bg-[var(--n-card)] border-[var(--n-border-2)] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[var(--hig-accent)]" /> Add Azure
              Credential
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Connect your Azure subscription using your preferred auth method
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={addCredential} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Display Name
              </Label>
              <Input
                value={credForm.name}
                onChange={(e) =>
                  setCredForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="e.g., Production Subscription"
                required
                data-testid="cred-name-input"
                className="bg-[var(--n-bg)] border-[var(--n-border-2)]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Authentication Method
              </Label>
              <Select
                value={credForm.auth_method}
                onValueChange={(v) =>
                  setCredForm((p) => ({ ...p, auth_method: v }))
                }
              >
                <SelectTrigger
                  className="bg-[var(--n-bg)] border-[var(--n-border-2)]"
                  data-testid="auth-method-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="service_principal">Service Principal (Client Secret)</SelectItem>
                  <SelectItem value="managed_identity">Managed Identity (Zero Secrets)</SelectItem>
                  <SelectItem value="workload_identity">Workload Identity Federation</SelectItem>
                  <SelectItem value="certificate">Certificate-Based Auth</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Subscription ID
              </Label>
              <Input
                value={credForm.subscription_id}
                onChange={(e) =>
                  setCredForm((p) => ({
                    ...p,
                    subscription_id: e.target.value,
                  }))
                }
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx"
                required
                data-testid="cred-subscription-input"
                className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-xs"
              />
            </div>

            {credForm.auth_method !== "managed_identity" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Tenant ID
                  </Label>
                  <Input
                    value={credForm.tenant_id}
                    onChange={(e) =>
                      setCredForm((p) => ({ ...p, tenant_id: e.target.value }))
                    }
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx"
                    required
                    data-testid="cred-tenant-input"
                    className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-xs"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Client ID (Application ID)
                  </Label>
                  <Input
                    value={credForm.client_id}
                    onChange={(e) =>
                      setCredForm((p) => ({ ...p, client_id: e.target.value }))
                    }
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    required
                    data-testid="cred-clientid-input"
                    className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-xs"
                  />
                </div>
              </div>
            )}

            {credForm.auth_method === "managed_identity" && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Client ID (optional — for user-assigned identity)
                </Label>
                <Input
                  value={credForm.client_id}
                  onChange={(e) =>
                    setCredForm((p) => ({ ...p, client_id: e.target.value }))
                  }
                  placeholder="Leave empty for system-assigned identity"
                  data-testid="cred-clientid-mi-input"
                  className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-xs"
                />
              </div>
            )}

            {credForm.auth_method === "service_principal" && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Client Secret
                </Label>
                <div className="relative">
                  <Input
                    type={showSecret ? "text" : "password"}
                    value={credForm.client_secret}
                    onChange={(e) =>
                      setCredForm((p) => ({
                        ...p,
                        client_secret: e.target.value,
                      }))
                    }
                    placeholder="Enter client secret"
                    required
                    data-testid="cred-secret-input"
                    className="bg-[var(--n-bg)] border-[var(--n-border-2)] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showSecret ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {credForm.auth_method === "certificate" && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Certificate (PEM format)
                </Label>
                <textarea
                  value={credForm.certificate_data}
                  onChange={(e) =>
                    setCredForm((p) => ({
                      ...p,
                      certificate_data: e.target.value,
                    }))
                  }
                  placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                  required
                  data-testid="cred-certificate-input"
                  className="w-full h-24 bg-[var(--n-bg)] border border-[var(--n-border-2)] rounded-md p-3 text-xs font-mono text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:border-[var(--hig-accent)]/40"
                />
              </div>
            )}

            <div className={`rounded-lg p-3 flex gap-2 ${
              credForm.auth_method === "managed_identity"
                ? "bg-emerald-500/5 border border-emerald-500/20"
                : "bg-amber-500/5 border border-amber-500/20"
            }`}>
              {credForm.auth_method === "managed_identity" ? (
                <>
                  <Shield className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-emerald-600 dark:text-emerald-400/80">
                    Managed Identity requires no stored secrets. The app must run on an Azure resource (VM, App Service, AKS) with an assigned identity that has Reader access.
                  </p>
                </>
              ) : credForm.auth_method === "workload_identity" ? (
                <>
                  <Shield className="w-4 h-4 text-violet-600 dark:text-violet-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-violet-600 dark:text-violet-400/80">
                    Workload Identity Federation uses federated tokens from your identity provider. No secrets are stored. Configure the federated credential in Azure AD.
                  </p>
                </>
              ) : credForm.auth_method === "certificate" ? (
                <>
                  <Shield className="w-4 h-4 text-[var(--hig-accent)] shrink-0 mt-0.5" />
                  <p className="text-xs text-[var(--hig-accent)]/80">
                    Certificate-based auth is more secure than client secrets. Upload your .pem certificate. Ensure the certificate is registered in your Azure AD app registration.
                  </p>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-600 dark:text-amber-400/80">
                    Client secrets are stored in the database. Consider using Managed Identity or Certificate auth for stronger security. Ensure this Service Principal has Reader access.
                  </p>
                </>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddForm(false)}
                className="flex-1 border-[var(--n-border-2)]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-credential-button"
                className="flex-1 bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold"
              >
                {saving ? "Saving..." : "Save Credential"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────── Notifications Tab ─────────────── */
const NOTIFICATION_TYPES = [
  { key: "password_reset", label: "Password Reset", desc: "Send email when password reset is requested", icon: KeyRound },
  { key: "drift_alerts", label: "Drift Alerts", desc: "Email admins when credential drift is detected", icon: AlertTriangle },
  { key: "billing_receipts", label: "Billing Receipts", desc: "Send payment confirmation emails", icon: Mail },
  { key: "welcome_email", label: "Welcome Email", desc: "Send welcome email to new users on registration", icon: Mail },
];

function NotificationsTab({ api, settings, updateSettings, isAdmin }) {
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const notifPrefs = settings.email_notifications || {};
  const emailMode = settings.email_mode || "mock";

  useEffect(() => {
    (async () => {
      try {
        const res = await api().get("/email/status");
        setEmailStatus(res.data);
      } catch (e) { /* ignore */ }
    })();
  }, [api]);

  const loadEmailLog = async () => {
    setLoadingLog(true);
    try {
      const res = await api().get("/email/log?limit=20");
      setEmailLog(res.data.logs);
    } catch (e) { /* ignore */ }
    finally { setLoadingLog(false); }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const res = await api().post("/email/test");
      toast.success(`Test email ${res.data.status === "mock_sent" ? "logged (mock mode)" : "sent"} to ${res.data.to}`);
      loadEmailLog();
    } catch (e) { toast.error("Failed to send test email"); }
    finally { setSendingTest(false); }
  };

  const toggleNotification = (key, checked) => {
    const updated = { ...notifPrefs, [key]: checked };
    updateSettings({ email_notifications: updated });
  };

  return (
    <div className="space-y-4" data-testid="notifications-tab">
      {/* Email Service Status */}
      <Card className="bg-[var(--n-surface)] border-[var(--n-border-2)]">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[var(--hig-accent)]" />
              <span className="text-sm font-medium">Email Service</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                emailMode === "live"
                  ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                  : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
              }`}>
                {emailMode === "live" ? "Live (Azure Communication Services)" : "Mock Mode"}
              </span>
            </div>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={sendTestEmail}
                disabled={sendingTest}
                className="text-xs"
                data-testid="send-test-email-btn"
              >
                {sendingTest ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Mail className="w-3 h-3 mr-1" />}
                {sendingTest ? "Sending..." : "Send Test Email"}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {emailMode === "live"
              ? "Emails are being sent via Azure Communication Services."
              : "ACS not configured. Emails are logged locally. Set ACS_CONNECTION_STRING and ACS_SENDER_EMAIL to enable live delivery."}
          </p>
          {emailStatus && (
            <p className="text-[10px] text-muted-foreground">
              Total emails processed: <span className="text-foreground font-mono">{emailStatus.total_sent}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Bell className="w-4 h-4 text-[var(--hig-accent)]" /> Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {NOTIFICATION_TYPES.map((nt) => (
            <div key={nt.key} className="flex items-center justify-between" data-testid={`notif-toggle-${nt.key}`}>
              <div className="flex items-center gap-3">
                <nt.icon className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{nt.label}</p>
                  <p className="text-xs text-muted-foreground">{nt.desc}</p>
                </div>
              </div>
              <Switch
                checked={notifPrefs[nt.key] !== false}
                onCheckedChange={(checked) => toggleNotification(nt.key, checked)}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Email Log (admin only) */}
      {isAdmin && (
        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="w-4 h-4 text-[var(--hig-accent)]" /> Email Log
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={loadEmailLog} disabled={loadingLog} className="text-xs" data-testid="load-email-log-btn">
                {loadingLog ? <Loader2 className="w-3 h-3 animate-spin" /> : "Load Log"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {emailLog.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">Click "Load Log" to view recent emails</p>
            ) : (
              <div className="space-y-2">
                {emailLog.map((log, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-[var(--n-hover)] border border-[var(--n-border)] text-xs" data-testid={`email-log-${i}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        log.status === "sent" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
                        log.status === "mock_sent" ? "bg-amber-500/10 text-amber-600 dark:text-amber-400" :
                        "bg-red-500/10 text-red-600 dark:text-red-400"
                      }`}>
                        {log.status === "mock_sent" ? "mock" : log.status}
                      </span>
                      <span className="text-[var(--n-text-muted)] truncate">{log.to}</span>
                      <span className="text-muted-foreground truncate hidden sm:inline">{log.subject}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className="bg-white/5 text-muted-foreground text-[10px]">{log.category}</Badge>
                      <span className="text-muted-foreground text-[10px]">{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ─────────────── Appearance Tab ─────────────── */
const FONT_OPTIONS = [
  { key: "system", label: "System Default", desc: "SF Pro / -apple-system", sample: "-apple-system, 'SF Pro Display', 'Helvetica Neue', system-ui, sans-serif" },
  { key: "inter", label: "Inter", desc: "Clean geometric sans-serif", sample: "'Inter', system-ui, sans-serif" },
  { key: "jakarta", label: "Plus Jakarta Sans", desc: "Modern rounded display font", sample: "'Plus Jakarta Sans', system-ui, sans-serif" },
];

function AppearanceTab() {
  const { theme, font, setTheme, setFont } = useTheme();

  return (
    <div className="space-y-6" data-testid="appearance-settings">
      {/* Theme Selection */}
      <Card className="bg-[var(--n-card)] border-[var(--n-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold font-heading">Theme</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: "system", label: "Auto", desc: "Follows OS setting" },
              { key: "light", label: "Light", desc: "Always light" },
              { key: "dark", label: "Dark", desc: "Always dark" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTheme(t.key)}
                data-testid={`theme-option-${t.key}`}
                className={`p-4 rounded-[10px] border-2 transition-all text-left ${
                  theme === t.key
                    ? "border-[var(--hig-accent)] bg-[var(--hig-accent-subtle)]"
                    : "border-[var(--n-border)] hover:border-[var(--n-border-3)]"
                }`}
              >
                <div className={`w-10 h-6 rounded-[6px] mb-2 ${
                  t.key === "light" ? "bg-gray-100 border border-gray-200" :
                  t.key === "dark" ? "bg-gray-900 border border-gray-700" :
                  "bg-gradient-to-r from-gray-100 to-gray-900 border border-gray-300"
                }`} />
                <p className="text-[13px] font-medium">{t.label}</p>
                <p className="text-[11px] text-[var(--n-text-muted)]">{t.desc}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Font Selection */}
      <Card className="bg-[var(--n-card)] border-[var(--n-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold font-heading">Font Family</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {FONT_OPTIONS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFont(f.key)}
                data-testid={`font-option-${f.key}`}
                className={`w-full p-4 rounded-[10px] border-2 transition-all text-left ${
                  font === f.key
                    ? "border-[var(--hig-accent)] bg-[var(--hig-accent-subtle)]"
                    : "border-[var(--n-border)] hover:border-[var(--n-border-3)]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-semibold" style={{ fontFamily: f.sample }}>{f.label}</p>
                    <p className="text-[11px] text-[var(--n-text-muted)]">{f.desc}</p>
                  </div>
                  <p className="text-lg font-semibold text-[var(--n-text-muted)]" style={{ fontFamily: f.sample }}>Aa</p>
                </div>
                <p className="text-[12px] text-[var(--n-text-secondary)] mt-2" style={{ fontFamily: f.sample }}>
                  The quick brown fox jumps over the lazy dog. 0123456789
                </p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─────────────── Configurations Tab (BYOK) ─────────────── */
const PROVIDER_META = {
  gemini: { icon: "G", color: "#4285f4", keyHelp: "Get a key from", url: "https://aistudio.google.com/apikey", urlLabel: "Google AI Studio", placeholder: "AIzaSy..." },
  openai: { icon: "O", color: "#10a37f", keyHelp: "Get a key from", url: "https://platform.openai.com/api-keys", urlLabel: "OpenAI Platform", placeholder: "sk-..." },
  anthropic: { icon: "A", color: "#cc785c", keyHelp: "Get a key from", url: "https://console.anthropic.com/settings/keys", urlLabel: "Anthropic Console", placeholder: "sk-ant-..." },
};

function ConfigurationsTab({ api, isAdmin }) {
  const [llmConfig, setLlmConfig] = useState(null);
  const [keys, setKeys] = useState({ gemini: "", openai: "", anthropic: "" });
  const [showKeys, setShowKeys] = useState({});
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api().get("/settings/llm").then((r) => {
      setLlmConfig(r.data);
      if (r.data.active_provider) setSelectedProvider(r.data.active_provider);
      if (r.data.active_model) setSelectedModel(r.data.active_model);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  const saveProvider = async (providerId) => {
    const keyFieldMap = { gemini: "gemini_api_key", openai: "openai_api_key", anthropic: "anthropic_api_key" };
    const keyField = keyFieldMap[providerId];
    const keyValue = keys[providerId]?.trim();
    if (!keyValue) return;

    setSaving(true);
    try {
      const models = llmConfig?.providers?.[providerId]?.models || [];
      const model = selectedProvider === providerId && selectedModel ? selectedModel : models[0]?.id || "";
      const r = await api().put("/settings/llm", {
        [keyField]: keyValue,
        active_provider: providerId,
        active_model: model,
      });
      setLlmConfig(r.data);
      setSelectedProvider(r.data.active_provider);
      setSelectedModel(r.data.active_model);
      setKeys((k) => ({ ...k, [providerId]: "" }));
      toast.success(`${llmConfig?.providers?.[providerId]?.label} key saved and activated.`);
    } catch {
      toast.error("Failed to save key");
    } finally {
      setSaving(false);
    }
  };

  const activateProvider = async (providerId) => {
    setSaving(true);
    try {
      const models = llmConfig?.providers?.[providerId]?.models || [];
      const r = await api().put("/settings/llm", {
        active_provider: providerId,
        active_model: models[0]?.id || "",
      });
      setLlmConfig(r.data);
      setSelectedProvider(r.data.active_provider);
      setSelectedModel(r.data.active_model);
      toast.success(`Switched to ${llmConfig?.providers?.[providerId]?.label}`);
    } catch {
      toast.error("Failed to switch provider");
    } finally {
      setSaving(false);
    }
  };

  const updateModel = async (model) => {
    setSelectedModel(model);
    setSaving(true);
    try {
      const r = await api().put("/settings/llm", { active_model: model });
      setLlmConfig(r.data);
      toast.success("Model updated");
    } catch {
      toast.error("Failed to update model");
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    setSaving(true);
    try {
      const r = await api().delete("/settings/llm/byok");
      setLlmConfig(r.data);
      setSelectedProvider("");
      setSelectedModel("");
      toast.success("All custom keys removed. Using Platform Key.");
    } catch {
      toast.error("Failed to remove keys");
    } finally {
      setSaving(false);
    }
  };

  const testKey = async (providerId) => {
    const keyValue = keys[providerId]?.trim();
    if (!keyValue) { toast.error("Enter a key first"); return; }
    setTesting((t) => ({ ...t, [providerId]: "loading" }));
    try {
      const models = llmConfig?.providers?.[providerId]?.models || [];
      const model = selectedProvider === providerId && selectedModel ? selectedModel : models[0]?.id || "";
      const r = await api().post("/settings/llm/test", { provider: providerId, api_key: keyValue, model });
      if (r.data.status === "success") {
        setTesting((t) => ({ ...t, [providerId]: "success" }));
        toast.success(r.data.message);
      } else {
        setTesting((t) => ({ ...t, [providerId]: "error" }));
        toast.error(r.data.message);
      }
    } catch {
      setTesting((t) => ({ ...t, [providerId]: "error" }));
      toast.error("Test failed");
    }
    setTimeout(() => setTesting((t) => ({ ...t, [providerId]: null })), 3000);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[var(--n-text-muted)]" /></div>;

  const source = llmConfig?.active_source || "none";
  const activeProviderLabel = llmConfig?.providers?.[llmConfig?.active_provider]?.label;
  const hasAnyByok = Object.values(llmConfig?.providers || {}).some((p) => p.has_key);

  return (
    <div className="space-y-6" data-testid="configurations-settings">
      {/* Active Status */}
      <Card className="bg-[var(--n-card)] border-[var(--n-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold font-heading">AI Engine Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 p-4 rounded-[10px] border border-[var(--n-border)]">
            <div className={`w-2.5 h-2.5 rounded-full ${source !== "none" ? "bg-[var(--hig-success)]" : "bg-[var(--hig-danger)]"}`} />
            <div className="flex-1">
              <p className="text-sm font-medium" data-testid="ai-status-label">
                {source === "byok"
                  ? `Using ${activeProviderLabel || "Custom Key"}`
                  : source === "platform"
                  ? "Using Platform Key"
                  : "No AI Key Configured"}
              </p>
              <p className="text-xs text-[var(--n-text-muted)] mt-0.5" data-testid="ai-status-detail">
                {source === "byok"
                  ? `Model: ${llmConfig?.active_model || "auto"}`
                  : source === "platform"
                  ? "Platform key is active. AI features are fully operational."
                  : "Add an API key to enable AI-powered insights, anomaly detection, and briefings."}
              </p>
            </div>
            {source !== "none" && (
              <Badge className="bg-[var(--hig-success)]/10 text-[var(--hig-success)] border-[var(--hig-success)]/20">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Active
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* BYOK Providers */}
      <Card className="bg-[var(--n-card)] border-[var(--n-border)]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold font-heading">Bring Your Own Key (Optional)</CardTitle>
            {hasAnyByok && (
              <Button variant="ghost" size="sm" onClick={clearAll} disabled={saving} className="text-xs text-[var(--hig-danger)]" data-testid="clear-all-byok-btn">
                Remove All Keys
              </Button>
            )}
          </div>
          <p className="text-xs text-[var(--n-text-muted)] mt-1">
            Provide your own LLM API key to use instead of the platform key. Select a provider and model below.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(llmConfig?.providers || {}).map(([pid, pinfo]) => {
            const meta = PROVIDER_META[pid] || {};
            const isActive = source === "byok" && llmConfig?.active_provider === pid;
            const hasKey = pinfo.has_key;
            const maskedKey = llmConfig?.[`${pid === "gemini" ? "gemini" : pid === "openai" ? "openai" : "anthropic"}_api_key`] || "";

            return (
              <div
                key={pid}
                className={`rounded-[10px] border-2 p-4 transition-all ${
                  isActive ? "border-[var(--hig-accent)] bg-[var(--hig-accent-subtle)]" : "border-[var(--n-border)]"
                }`}
                data-testid={`provider-${pid}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: meta.color }}
                  >
                    {meta.icon}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{pinfo.label}</p>
                    {hasKey && <p className="text-[10px] text-[var(--n-text-muted)] font-mono">{maskedKey}</p>}
                  </div>
                  {hasKey && !isActive && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => activateProvider(pid)}
                      disabled={saving}
                      className="text-xs border-[var(--hig-accent)]/30 text-[var(--hig-accent)]"
                      data-testid={`activate-${pid}`}
                    >
                      Activate
                    </Button>
                  )}
                  {isActive && (
                    <Badge className="bg-[var(--hig-accent)]/10 text-[var(--hig-accent)] border-[var(--hig-accent)]/20 text-xs">Active</Badge>
                  )}
                </div>

                {/* Model selector — only for active provider with key */}
                {isActive && hasKey && (
                  <div className="mb-3">
                    <Label className="text-[11px] text-[var(--n-text-muted)] uppercase tracking-wider">Model</Label>
                    <div className="flex gap-2 mt-1.5">
                      {pinfo.models.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => updateModel(m.id)}
                          disabled={saving}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                            selectedModel === m.id
                              ? "bg-[var(--hig-accent)] text-white border-[var(--hig-accent)]"
                              : "border-[var(--n-border-2)] text-[var(--n-text-secondary)] hover:border-[var(--hig-accent)]/40"
                          }`}
                          data-testid={`model-${m.id}`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key input */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showKeys[pid] ? "text" : "password"}
                      value={keys[pid] || ""}
                      onChange={(e) => setKeys((k) => ({ ...k, [pid]: e.target.value }))}
                      placeholder={hasKey ? "Enter new key to replace..." : meta.placeholder}
                      className="bg-[var(--n-bg)] border-[var(--n-border-2)] pr-10 h-9 text-xs font-mono"
                      data-testid={`key-input-${pid}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowKeys((s) => ({ ...s, [pid]: !s[pid] }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--n-text-muted)]"
                    >
                      {showKeys[pid] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => testKey(pid)}
                    disabled={!keys[pid]?.trim() || testing[pid] === "loading"}
                    className={`h-9 px-3 text-xs border-[var(--n-border-2)] hig-btn ${
                      testing[pid] === "success" ? "border-[var(--hig-success)] text-[var(--hig-success)]" :
                      testing[pid] === "error" ? "border-[var(--hig-danger)] text-[var(--hig-danger)]" : ""
                    }`}
                    data-testid={`test-key-${pid}`}
                  >
                    {testing[pid] === "loading" ? <Loader2 className="w-3 h-3 animate-spin" /> :
                     testing[pid] === "success" ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                     testing[pid] === "error" ? <AlertTriangle className="w-3.5 h-3.5" /> : "Test"}
                  </Button>
                  <Button
                    onClick={() => saveProvider(pid)}
                    disabled={!keys[pid]?.trim() || saving}
                    className="bg-[var(--hig-accent)] hover:opacity-90 text-white h-9 px-4 text-xs hig-btn"
                    data-testid={`save-key-${pid}`}
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                  </Button>
                </div>
                <p className="text-[10px] text-[var(--n-text-muted)] mt-1.5">
                  {meta.keyHelp}{" "}
                  <a href={meta.url} target="_blank" rel="noreferrer" className="text-[var(--hig-accent)] hover:underline">
                    {meta.urlLabel}
                  </a>
                </p>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}



/* ─────────────── User Activity Tab (non-admin) ─────────────── */
const ACTIVITY_SEVERITY = {
  critical: "bg-red-500/20 text-red-600 dark:text-red-400",
  warning: "bg-amber-500/20 text-amber-600 dark:text-amber-400",
  info: "bg-[var(--hig-accent)]/20 text-[var(--hig-accent)]",
};

const ACTIVITY_LABELS = {
  "user.register": "Account Created",
  "auth.login": "Signed In",
  "auth.login_failed": "Failed Sign In",
  "auth.password_reset_requested": "Password Reset Requested",
  "auth.password_reset": "Password Changed",
  "admin.plan_change": "Plan Changed",
  "admin.bypass_change": "Access Updated",
  "admin.trial_start": "Trial Started",
  "admin.role_change": "Role Changed",
};

function UserActivityTab({ api }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api().get("/audit/my-activity?limit=50");
        setLogs(res.data.logs);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [api]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-[var(--hig-accent)]" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="user-activity-tab">
      <p className="text-sm text-muted-foreground">Your recent activity</p>
      {logs.length === 0 ? (
        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
          <CardContent className="p-8 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No activity recorded yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => (
            <div key={log.id} className="flex items-start gap-3 p-3 rounded-xl bg-[var(--n-hover)] border border-[var(--n-border)]" data-testid={`activity-row-${log.id}`}>
              <Badge className={`text-[10px] shrink-0 mt-0.5 ${ACTIVITY_SEVERITY[log.severity] || ACTIVITY_SEVERITY.info}`}>
                {log.severity}
              </Badge>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{ACTIVITY_LABELS[log.action] || log.action}</p>
                {log.details && Object.keys(log.details).length > 0 && (
                  <p className="text-xs text-muted-foreground truncate">
                    {Object.entries(log.details).filter(([,v]) => v != null).map(([k,v]) => `${k}: ${v}`).join(" · ")}
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {new Date(log.timestamp).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────── Admin Audit Log Tab ─────────────── */
const SEVERITY_STYLES = {
  critical: "bg-red-500/20 text-red-600 dark:text-red-400",
  warning: "bg-amber-500/20 text-amber-600 dark:text-amber-400",
  info: "bg-[var(--hig-accent)]/20 text-[var(--hig-accent)]",
};

const ACTION_LABELS = {
  "user.register": "User Registered",
  "auth.login": "Login",
  "auth.login_failed": "Failed Login",
  "auth.password_reset_requested": "Reset Requested",
  "auth.password_reset": "Password Reset",
  "admin.password_reset": "Admin Reset Password",
  "admin.account_unlock": "Account Unlocked",
  "admin.plan_change": "Plan Changed",
  "admin.bypass_change": "Bypass Changed",
  "admin.trial_start": "Trial Started",
  "admin.role_change": "Role Changed",
  "admin.feature_override": "Feature Override",
};

const RISK_COLORS = {
  CRITICAL: "bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30",
  HIGH: "bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30",
  MEDIUM: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  LOW: "bg-[var(--hig-accent)]/20 text-[var(--hig-accent)] border-[var(--hig-accent)]/30",
};

function AuditLogTab({ api }) {
  const [subTab, setSubTab] = useState("overview");

  const subTabs = [
    { id: "overview", label: "AI Overview" },
    { id: "table", label: "Data Table" },
    { id: "alerts", label: "Security Alerts" },
  ];

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <div className="flex gap-1 p-1 bg-[var(--n-hover)] rounded-lg border border-[var(--n-border)] w-fit">
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-3 py-1.5 rounded-md text-xs transition-all ${
              subTab === t.id
                ? "bg-[var(--hig-accent)]/10 text-[var(--hig-accent)] border border-[var(--hig-accent)]/20"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`audit-subtab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "overview" && <AuditOverview api={api} />}
      {subTab === "table" && <AuditDataTable api={api} />}
      {subTab === "alerts" && <AuditSecurityAlerts api={api} />}
    </div>
  );
}

function AuditOverview({ api }) {
  const [summary, setSummary] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api().get("/audit/summary");
        setSummary(res.data);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [api]);

  const runAiAnalysis = async () => {
    setAiLoading(true);
    try {
      const res = await api().post("/audit/ai-analysis");
      setAiAnalysis(res.data);
    } catch (e) { toast.error("AI analysis failed"); }
    finally { setAiLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[var(--hig-accent)]" /></div>;

  const today = summary?.today || {};

  return (
    <div className="space-y-4" data-testid="audit-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Events Today", value: today.total || 0, color: "cyan" },
          { label: "Failed Logins", value: today.failed_logins || 0, color: today.failed_logins > 3 ? "red" : "amber" },
          { label: "Admin Actions", value: today.admin_actions || 0, color: "violet" },
          { label: "Critical", value: today.critical || 0, color: today.critical > 0 ? "red" : "emerald" },
        ].map((m) => (
          <div key={m.label} className="glass-card rounded-xl p-4">
            <p className="text-xs text-muted-foreground">{m.label}</p>
            <p className={`text-2xl font-bold text-${m.color}-400`}>{m.value}</p>
          </div>
        ))}
      </div>

      <div className="glass-card rounded-xl p-4">
        <p className="text-xs text-muted-foreground mb-3">Severity Breakdown</p>
        <div className="flex gap-3">
          {Object.entries(summary?.by_severity || {}).map(([sev, count]) => (
            <div key={sev} className="flex items-center gap-2">
              <Badge className={`${SEVERITY_STYLES[sev] || SEVERITY_STYLES.info} text-[10px]`}>{sev}</Badge>
              <span className="text-sm font-mono">{count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="glass-card rounded-xl p-4">
        <p className="text-xs text-muted-foreground mb-3">Top Actions ({summary?.total_events || 0} total)</p>
        <div className="space-y-1.5">
          {Object.entries(summary?.by_action || {}).slice(0, 8).map(([action, count]) => {
            const maxCount = Math.max(...Object.values(summary?.by_action || { x: 1 }));
            return (
              <div key={action} className="flex items-center gap-3">
                <span className="text-xs text-[var(--n-text-muted)] w-40 truncate">{ACTION_LABELS[action] || action}</span>
                <div className="flex-1 h-2 bg-[var(--n-hover)] rounded-full overflow-hidden">
                  <div className="h-full bg-[var(--hig-accent)]/40 rounded-full" style={{ width: `${(count / maxCount) * 100}%` }} />
                </div>
                <span className="text-xs font-mono text-[var(--n-text-muted)] w-8 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            AI Security Analysis
          </p>
          <Button variant="outline" size="sm" className="text-xs" onClick={runAiAnalysis} disabled={aiLoading} data-testid="run-ai-analysis-btn">
            {aiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
            {aiLoading ? "Analyzing..." : "Run Analysis"}
          </Button>
        </div>
        {aiAnalysis ? (
          <div className="space-y-3">
            <p className="text-sm text-[var(--n-text-secondary)]">{aiAnalysis.summary}</p>
            {aiAnalysis.findings?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Key Findings</p>
                <ul className="space-y-1">
                  {aiAnalysis.findings.map((f, i) => (
                    <li key={i} className="text-xs text-[var(--n-text-muted)] flex items-start gap-2">
                      <span className="text-[var(--hig-accent)] mt-0.5">-</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {aiAnalysis.recommendations?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Recommendations</p>
                <ul className="space-y-1">
                  {aiAnalysis.recommendations.map((r, i) => (
                    <li key={i} className="text-xs text-[var(--n-text-muted)] flex items-start gap-2">
                      <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">-</span>{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Click "Run Analysis" to get AI-powered insights on your audit trail</p>
        )}
      </div>
    </div>
  );
}

function AuditDataTable({ api }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const pageSize = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: pageSize, offset: page * pageSize });
      if (filter) params.append("action", filter);
      if (actorFilter) params.append("actor_email", actorFilter);
      if (severityFilter !== "all") params.append("severity", severityFilter);
      const res = await api().get(`/audit/logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (e) { toast.error("Failed to load logs"); }
    finally { setLoading(false); }
  }, [api, filter, actorFilter, severityFilter, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-3" data-testid="audit-data-table">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Filter action..." value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }}
            className="pl-8 h-8 text-xs bg-[var(--n-hover)] border-[var(--n-border)]" data-testid="audit-filter-input" />
        </div>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Filter actor..." value={actorFilter} onChange={(e) => { setActorFilter(e.target.value); setPage(0); }}
            className="pl-8 h-8 text-xs bg-[var(--n-hover)] border-[var(--n-border)]" data-testid="audit-actor-filter" />
        </div>
        <Select value={severityFilter} onValueChange={(v) => { setSeverityFilter(v); setPage(0); }}>
          <SelectTrigger className="w-28 h-8 text-xs bg-[var(--n-hover)] border-[var(--n-border)]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="info">Info</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[10px] text-muted-foreground">{total} events</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[var(--hig-accent)]" /></div>
      ) : (
        <div className="border border-[var(--n-border)] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[var(--n-hover)] border-b border-[var(--n-border)]">
                <th className="text-left px-3 py-2 text-muted-foreground font-medium">Severity</th>
                <th className="text-left px-3 py-2 text-muted-foreground font-medium">Action</th>
                <th className="text-left px-3 py-2 text-muted-foreground font-medium hidden sm:table-cell">Actor</th>
                <th className="text-left px-3 py-2 text-muted-foreground font-medium hidden md:table-cell">Target</th>
                <th className="text-right px-3 py-2 text-muted-foreground font-medium">Time</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr
                    className="border-b border-white/[0.03] hover:bg-[var(--n-hover)] cursor-pointer transition-colors"
                    onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                    data-testid={`audit-row-${log.id}`}
                  >
                    <td className="px-3 py-2">
                      <Badge className={`text-[10px] ${SEVERITY_STYLES[log.severity] || SEVERITY_STYLES.info}`}>{log.severity}</Badge>
                    </td>
                    <td className="px-3 py-2 font-medium text-[var(--n-text-secondary)]">{ACTION_LABELS[log.action] || log.action}</td>
                    <td className="px-3 py-2 text-muted-foreground hidden sm:table-cell">{log.actor_email || "-"}</td>
                    <td className="px-3 py-2 text-muted-foreground hidden md:table-cell font-mono">{log.target_id?.slice(0, 8) || "-"}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                  </tr>
                  {expanded === log.id && (
                    <tr key={`${log.id}-detail`}>
                      <td colSpan={5} className="px-3 py-3 bg-white/[0.01]">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
                          <div><span className="text-muted-foreground">Event ID:</span><br/><span className="font-mono text-[var(--n-text-muted)]">{log.id?.slice(0, 12)}...</span></div>
                          <div><span className="text-muted-foreground">IP:</span><br/><span className="font-mono text-[var(--n-text-muted)]">{log.ip_address || "N/A"}</span></div>
                          <div><span className="text-muted-foreground">Actor ID:</span><br/><span className="font-mono text-[var(--n-text-muted)]">{log.actor_id?.slice(0, 12) || "N/A"}</span></div>
                          <div><span className="text-muted-foreground">Target Type:</span><br/><span className="text-[var(--n-text-muted)]">{log.target_type || "N/A"}</span></div>
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-2 p-2 rounded bg-[var(--n-hover)] border border-[var(--n-border)]">
                            <span className="text-[10px] text-muted-foreground">Details:</span>
                            <pre className="text-[11px] text-[var(--n-text-muted)] mt-1 whitespace-pre-wrap">{JSON.stringify(log.details, null, 2)}</pre>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > pageSize && (
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} className="text-xs">Previous</Button>
          <span className="text-xs text-muted-foreground">Page {page + 1} of {Math.ceil(total / pageSize)}</span>
          <Button variant="outline" size="sm" disabled={(page + 1) * pageSize >= total} onClick={() => setPage(page + 1)} className="text-xs">Next</Button>
        </div>
      )}
    </div>
  );
}

function AuditSecurityAlerts({ api }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const res = await api().post("/audit/ai-analysis");
      setAnalysis(res.data);
    } catch (e) { toast.error("Analysis failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="audit-security-alerts">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">AI-powered anomaly detection across your audit trail</p>
        <Button variant="outline" size="sm" className="text-xs" onClick={runAnalysis} disabled={loading} data-testid="scan-anomalies-btn">
          {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Shield className="w-3 h-3 mr-1" />}
          {loading ? "Scanning..." : "Scan for Anomalies"}
        </Button>
      </div>

      {analysis ? (
        <div className="space-y-4">
          <div className="glass-card rounded-xl p-4">
            <p className="text-sm text-[var(--n-text-secondary)]">{analysis.summary}</p>
          </div>

          {analysis.anomalies?.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Detected Anomalies</p>
              {analysis.anomalies.map((a, i) => (
                <div key={i} className={`glass-card rounded-xl p-4 border ${
                  a.risk === "CRITICAL" ? "border-red-500/20" :
                  a.risk === "HIGH" ? "border-amber-500/20" : "border-[var(--n-border)]"
                }`} data-testid={`anomaly-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={`text-[10px] border ${RISK_COLORS[a.risk] || RISK_COLORS.LOW}`}>{a.risk}</Badge>
                    <span className="text-sm font-medium">{a.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{a.detail}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="glass-card rounded-xl p-6 text-center">
              <ShieldCheck className="w-8 h-8 text-emerald-600 dark:text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-emerald-600 dark:text-emerald-400">No anomalies detected</p>
              <p className="text-xs text-muted-foreground mt-1">Your audit trail looks clean</p>
            </div>
          )}

          {analysis.findings?.length > 0 && (
            <div className="glass-card rounded-xl p-4">
              <p className="text-xs text-muted-foreground mb-2">Findings</p>
              <ul className="space-y-1">
                {analysis.findings.map((f, i) => (
                  <li key={i} className="text-xs text-[var(--n-text-muted)] flex items-start gap-2">
                    <span className="text-[var(--hig-accent)] shrink-0">-</span>{f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="glass-card rounded-xl p-8 text-center">
          <Shield className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-sm text-[var(--n-text-muted)]">Click "Scan for Anomalies" to run AI-powered security analysis</p>
          <p className="text-xs text-muted-foreground mt-1">Analyzes login patterns, privilege escalations, and suspicious activity</p>
        </div>
      )}
    </div>
  );
}
